import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["style", "className"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { attachmentCompose } from 'flow/actions/chat-actions';
import GenericListSkeleton from 'flow/components/loaders/GenericListSkeleton';
import { MessageThreadCheckProvider } from 'flow/contexts/MessageThreadCheckProvider';
import { useModal } from 'flow/contexts/ModalProvider';
import { useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import useRovingMessageList from 'flow/hooks/useRovingMessageList';
import { MAX_ALLOWED_ATTACHMENT_SIZE } from 'flow/utils/message-utils';
import ExternalChatMessage from 'flow/pages/messages/ExternalChatMessage';
import ChatMessageRow from './ChatMessageRow';
import ChatPostRow from './ChatPostRow';
import DateDivider from './DateDivider';
import UnreadDivider from './UnreadDivider';
import { useMessageListData } from './useMessageListData';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var SCROLL_READY_DELAY = 300;
var SCROLL_TOP_THRESHOLD = 200;
var AT_BOTTOM_THRESHOLD = 50;
var Scroller = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var style = _ref.style,
    className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded);
  // Whether the initial scroll position has settled. Until it has,
  // scrollbar-deferred keeps the thumb invisible so it does not flicker
  // while the list is being measured and moved; after it, CSS shows the
  // thumb. Both classes are needed: on its own, .scrollbar means the
  // ordinary always-visible thumb every other list has.
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    settled = _useState2[0],
    setSettled = _useState2[1];
  useEffect(function () {
    var timer = setTimeout(function () {
      return setSettled(true);
    }, SCROLL_READY_DELAY);
    return function () {
      return clearTimeout(timer);
    };
  }, []);
  return /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
    ref: ref,
    style: style
  }, props), {}, {
    className: twMerge(className, 'scrollbar scrollbar-deferred', settled && 'scrollbar-ready')
  }));
});
Scroller.displayName = 'Scroller';

/**
 * Splits the flat item list into one box per date, each starting with that
 * date's divider. Anything before the first divider - there should be nothing,
 * but the list is not the one deciding that - keeps its place in a leading box.
 */
export var groupItemsByDate = function groupItemsByDate(items) {
  var groups = [];
  var timesDateSeen = new Map();
  items.forEach(function (item) {
    if (item.type === 'date-divider' || groups.length === 0) {
      var key = 'before-first-date';
      if (item.type === 'date-divider') {
        var _timesDateSeen$get;
        /* A date can legitimately appear twice: the divider is emitted
           whenever the date changes from the previous item, and a
           failed send sorts after everything else regardless of when
           it was written - so a failed message from an earlier day
           reopens that day at the end. Keyed on the date alone, two
           sibling boxes would share a key and React could reuse a
           whole day's rows for the wrong day.
            Counted per date rather than by position in the list,
           because paging in history adds earlier days at the front:
           a positional key would change for every box below them, and
           React would remount the lot - restarting a playing video and
           re-requesting every image. Older messages for a day already
           loaded stay contiguous with it, so they add no occurrence
           and leave these keys alone. */
        var occurrence = (_timesDateSeen$get = timesDateSeen.get(item.date)) !== null && _timesDateSeen$get !== void 0 ? _timesDateSeen$get : 0;
        timesDateSeen.set(item.date, occurrence + 1);
        key = "".concat(item.date, "-").concat(occurrence);
      }
      groups.push({
        key: key,
        items: []
      });
    }
    groups[groups.length - 1].items.push(item);
  });
  return groups;
};

/**
 * Wrapper that observes its own content height. When the content grows
 * (e.g. a reaction animates open) while the scroller is at the bottom,
 * it adjusts scrollTop to keep the bottom pinned.
 */
var ContentWrapper = function ContentWrapper(_ref2) {
  var scrollerRef = _ref2.scrollerRef,
    atBottomRef = _ref2.atBottomRef,
    children = _ref2.children;
  var contentRef = useRef(null);
  var prevHeightRef = useRef(0);
  useEffect(function () {
    var el = contentRef.current;
    if (!el) return;
    var observer = new ResizeObserver(function () {
      var newHeight = el.offsetHeight;
      var delta = newHeight - prevHeightRef.current;
      prevHeightRef.current = newHeight;
      if (delta > 0 && atBottomRef.current && scrollerRef.current) {
        scrollerRef.current.scrollTop += delta;
      }
    });
    observer.observe(el);
    return function () {
      return observer.disconnect();
    };
  }, [scrollerRef, atBottomRef]);
  return /*#__PURE__*/_jsx("div", {
    ref: contentRef,
    className: "flex flex-col items-center",
    children: children
  });
};
var MessageList = function MessageList(_ref3) {
  var chatSessionKey = _ref3.chatSessionKey,
    _ref3$videoDark = _ref3.videoDark,
    videoDark = _ref3$videoDark === void 0 ? false : _ref3$videoDark;
  var dispatch = useAppDispatch();
  var _useModal = useModal(),
    openModal = _useModal.openModal;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var scrollerRef = useRef(null);
  // One Tab stop for the whole list, entered at the newest message;
  // the arrows move between messages.
  var rovingHandlers = useRovingMessageList(scrollerRef);
  var atBottomRef = useRef(true);
  var prevScrollHeightRef = useRef(0);
  var prevClientHeightRef = useRef(0);
  var prevFirstKeyRef = useRef();
  var prevLastKeyRef = useRef();
  var _useMessageListData = useMessageListData(chatSessionKey),
    items = _useMessageListData.items,
    ready = _useMessageListData.ready,
    lastMessageIsOwn = _useMessageListData.lastMessageIsOwn,
    markReadIfNeeded = _useMessageListData.markReadIfNeeded,
    loadOlderMessages = _useMessageListData.loadOlderMessages,
    isLoadingOlder = _useMessageListData.isLoadingOlder;

  // Reset at-bottom when switching chats so the new chat always starts
  // at bottom, regardless of where the previous chat was scrolled.
  var prevChatKeyRef = useRef(chatSessionKey);
  if (prevChatKeyRef.current !== chatSessionKey) {
    prevChatKeyRef.current = chatSessionKey;
    atBottomRef.current = true;
  }

  // Get the first message/post key for prepend detection
  var firstMsgKey = items.find(function (item) {
    return item.type === 'message' || item.type === 'post';
  });
  var currentFirstKey = firstMsgKey && (firstMsgKey.type === 'message' || firstMsgKey.type === 'post') ? firstMsgKey.chatMessageKey : undefined;

  // Newest message key, so a message of your own can be told apart from the
  // same message being re-rendered for some other reason.
  var lastMsgKey = _toConsumableArray(items).reverse().find(function (item) {
    return item.type === 'message' || item.type === 'post';
  });
  var currentLastKey = lastMsgKey && (lastMsgKey.type === 'message' || lastMsgKey.type === 'post') ? lastMsgKey.chatMessageKey : undefined;
  var isAtBottom = useCallback(function () {
    var el = scrollerRef.current;
    if (!el) return false;
    return el.scrollHeight - el.scrollTop - el.clientHeight < AT_BOTTOM_THRESHOLD;
  }, []);
  var scrollToBottom = useCallback(function () {
    var instant = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    var el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: instant ? 'instant' : 'smooth'
    });
  }, []);

  // Scroll to bottom on initial mount and when switching chats.
  useLayoutEffect(function () {
    if (ready) {
      var _scrollerRef$current$, _scrollerRef$current;
      scrollToBottom();
      atBottomRef.current = true;
      prevScrollHeightRef.current = (_scrollerRef$current$ = (_scrollerRef$current = scrollerRef.current) === null || _scrollerRef$current === void 0 ? void 0 : _scrollerRef$current.scrollHeight) !== null && _scrollerRef$current$ !== void 0 ? _scrollerRef$current$ : 0;
      markReadIfNeeded();
    }
    prevFirstKeyRef.current = currentFirstKey;
    prevLastKeyRef.current = currentLastKey;
  }, [chatSessionKey, ready]);

  // Handle new messages: scroll to bottom if already there
  // Handle prepend: preserve scroll position
  useLayoutEffect(function () {
    var el = scrollerRef.current;
    if (!el || !ready) return;

    // Only when the newest message is one that just arrived - otherwise a
    // rebuild for an unrelated reason would drag a reader back down.
    var sentByYou = lastMessageIsOwn && prevLastKeyRef.current !== currentLastKey;

    // Detect prepend: first message key changed
    if (prevFirstKeyRef.current && currentFirstKey && currentFirstKey !== prevFirstKeyRef.current) {
      var addedHeight = el.scrollHeight - prevScrollHeightRef.current;
      if (addedHeight > 0) {
        el.scrollTop += addedHeight;
      }
    } else if (atBottomRef.current || sentByYou) {
      // New message at the end - stay at bottom. A message of your own
      // brings you down to it however far back you were reading: you
      // just wrote it, so not showing it reads as the send having failed.
      scrollToBottom();
      atBottomRef.current = true;
      markReadIfNeeded();
    }
    prevScrollHeightRef.current = el.scrollHeight;
    prevFirstKeyRef.current = currentFirstKey;
    prevLastKeyRef.current = currentLastKey;
  }, [items]);

  // Re-pin when the viewport itself shrinks, which happens whenever something
  // takes height from the list: the composer growing to several lines, the
  // dialpad opening, the window resizing. Nothing here changes the content, so
  // ContentWrapper's observer never fires for it -- without this the newest
  // message slides behind the composer as you type.
  //
  // Deliberately does not consult atBottomRef: losing height pushes us further
  // from the bottom than AT_BOTTOM_THRESHOLD, so the resulting scroll event
  // clears that flag before this runs. Instead, measure against the height just
  // lost, which reconstructs whether we were at the bottom beforehand and does
  // not depend on the order the two events arrive in.
  //
  // Someone who had scrolled back to read is further away than the height lost,
  // so they stay where they are rather than being yanked to the end.
  useEffect(function () {
    var el = scrollerRef.current;
    if (!el) return;
    prevClientHeightRef.current = el.clientHeight;
    var observer = new ResizeObserver(function () {
      var heightLost = prevClientHeightRef.current - el.clientHeight;
      prevClientHeightRef.current = el.clientHeight;
      if (heightLost <= 0) return;
      var distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom <= heightLost + AT_BOTTOM_THRESHOLD) {
        scrollToBottom();
        atBottomRef.current = true;
        prevScrollHeightRef.current = el.scrollHeight;
      }
    });
    observer.observe(el);
    return function () {
      return observer.disconnect();
    };
    // `ready` gates whether the scroller is mounted at all - while the
    // skeleton is showing there is no element to observe, so this has to
    // re-run once it appears.
  }, [scrollToBottom, ready]);
  var handleScroll = useCallback(function () {
    var el = scrollerRef.current;
    if (!el) return;
    atBottomRef.current = isAtBottom();
    if (atBottomRef.current) {
      markReadIfNeeded();
    }
    if (el.scrollTop < SCROLL_TOP_THRESHOLD) {
      loadOlderMessages();
    }
  }, [isAtBottom, markReadIfNeeded, loadOlderMessages]);
  var handleDrop = useCallback(function (event) {
    event.preventDefault();
    var file = event.dataTransfer.files[0];
    if (file) {
      if (file.size > MAX_ALLOWED_ATTACHMENT_SIZE) {
        openModal('messageAttachmentTooLarge');
        return;
      }
      dispatch(attachmentCompose(chatSessionKey, {
        name: file.name,
        file: file
      }));
    }
  }, [chatSessionKey, dispatch, openModal]);
  var renderItem = function renderItem(item) {
    switch (item.type) {
      case 'date-divider':
        return /*#__PURE__*/_jsx(DateDivider, {
          date: item.date,
          videoDark: videoDark
        }, "date-".concat(item.date));
      case 'unread-divider':
        return /*#__PURE__*/_jsx(UnreadDivider, {
          videoDark: videoDark
        }, "unread-divider");
      case 'message':
        return /*#__PURE__*/_jsx(ChatMessageRow, {
          chatSessionKey: chatSessionKey,
          chatMessageKey: item.chatMessageKey,
          showAvatar: item.showAvatar,
          videoDark: videoDark
        }, "msg-".concat(item.chatMessageKey));
      case 'post':
        return /*#__PURE__*/_jsx(ChatPostRow, {
          chatSessionKey: chatSessionKey,
          chatMessageKey: item.chatMessageKey,
          videoDark: videoDark
        }, "post-".concat(item.chatMessageKey));
      case 'external':
        // Rendered from the item rather than selected by key: guest
        // messages are relay state and never enter state.chats.
        return /*#__PURE__*/_jsx(ExternalChatMessage, {
          message: item.message,
          showAvatar: item.showAvatar,
          videoDark: videoDark
        }, "ext-".concat(item.message.get('key')));
    }
  };
  var skeleton = /*#__PURE__*/_jsx("div", {
    className: "relative flex w-full flex-col items-center",
    children: /*#__PURE__*/_jsx(GenericListSkeleton, {
      isLarge: true,
      count: 5,
      videoDark: videoDark
    })
  });
  if (!ready) {
    return /*#__PURE__*/_jsx("main", {
      "data-test-id": "message-list",
      className: "scrollbar flex-1 items-end pt-6",
      children: skeleton
    });
  }
  return /*#__PURE__*/_jsx(Scroller, _objectSpread(_objectSpread({
    ref: scrollerRef,
    "data-test-id": "message-list",
    role: "feed",
    "aria-label": i18next.t('route.messages'),
    "aria-busy": isLoadingOlder
  }, rovingHandlers), {}, {
    onScroll: handleScroll,
    onDrop: handleDrop,
    onDragOver: function onDragOver(e) {
      return e.preventDefault();
    },
    className: "flex-1 items-end pt-6",
    children: /*#__PURE__*/_jsx(MessageThreadCheckProvider, {
      value: false,
      children: /*#__PURE__*/_jsxs(ContentWrapper, {
        scrollerRef: scrollerRef,
        atBottomRef: atBottomRef,
        children: [/*#__PURE__*/_jsx("div", {
          className: "w-full max-w-2xl",
          children: groupItemsByDate(items).map(function (group) {
            return (
              /*#__PURE__*/
              /* One box per day, so the day's pinned header is
                 pushed out by the bottom of its own day rather
                 than staying stuck for the whole conversation.
                 A sticky element can only travel within its
                 nearest block ancestor, so without this the
                 headers pile up at the same offset and the
                 newest simply paints over the rest. */
              _jsx("div", {
                children: group.items.map(renderItem)
              }, group.key)
            );
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "h-3"
        })]
      })
    })
  }));
};
export default MessageList;